import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

/**
 * Sistema global de diálogos de confirmação — substitui window.confirm() nativo
 * (que é feio, inconsistente entre browsers e não respeita o tema do sistema).
 *
 * USO:
 *   import { useConfirm } from '../../components/ConfirmDialog';
 *   const confirm = useConfirm();
 *
 *   // Drop-in replacement do window.confirm():
 *   if (!(await confirm('Tem certeza?'))) return;
 *
 *   // Versão rica com título, variante e textos customizados:
 *   const ok = await confirm({
 *     title: 'Excluir cliente',
 *     message: 'Esta ação é irreversível...',
 *     confirmText: 'Sim, excluir',
 *     cancelText: 'Cancelar',
 *     variant: 'danger',          // 'default' | 'danger' | 'warning'
 *     items: [                     // opcional: bullet list visual
 *       'Usuários serão desativados',
 *       'Agendamentos cancelados',
 *       'Faturas pendentes canceladas',
 *     ],
 *     hint: 'Se for temporário, use Suspender.',
 *   });
 *
 * SETUP:
 *   Envolva a App com <ConfirmProvider>. Já é feito em App.js.
 */

const ConfirmContext = createContext(null);

const VARIANT_STYLES = {
  default: {
    icon: '❓',
    accent: 'var(--primary)',
    confirmClass: 'btn-primary',
  },
  danger: {
    icon: '⚠️',
    accent: '#dc2626',
    confirmClass: 'btn-danger',
  },
  warning: {
    icon: '⚠️',
    accent: '#f59e0b',
    confirmClass: 'btn-warning',
  },
  success: {
    icon: '✅',
    accent: '#16a34a',
    confirmClass: 'btn-success',
  },
};

export function ConfirmProvider({ children }) {
  const [dialog, setDialog] = useState(null); // { opts, resolve }
  const resolverRef = useRef(null);

  // IMPORTANTE: handleResult PRECISA ser declarado ANTES do useEffect
  // que o usa na dependency array. Se ficar depois, dispara
  // "ReferenceError: Cannot access 'handleResult' before initialization"
  // (TDZ) no build de producao minificado, porque const nao e hoisted.
  const handleResult = useCallback((result) => {
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
    setDialog(null);
  }, []);

  // Sempre que um novo diálogo abre, guarda o resolver pra resolver no clique.
  useEffect(() => {
    if (dialog) resolverRef.current = dialog.resolve;
  }, [dialog]);

  // Atalhos de teclado: Esc = cancel, Enter = confirm
  useEffect(() => {
    if (!dialog) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleResult(false);
      } else if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
        e.preventDefault();
        handleResult(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [dialog, handleResult]);

  // Função chamada pelo hook. Aceita string (mensagem simples) ou objeto.
  const confirm = useCallback((input) => {
    const opts = typeof input === 'string' ? { message: input } : (input || {});
    return new Promise((resolve) => {
      setDialog({
        opts: {
          title: opts.title || 'Confirmação',
          message: opts.message || 'Tem certeza?',
          items: Array.isArray(opts.items) ? opts.items : null,
          hint: opts.hint || null,
          confirmText: opts.confirmText || 'Confirmar',
          cancelText: opts.cancelText || 'Cancelar',
          variant: VARIANT_STYLES[opts.variant] ? opts.variant : 'default',
        },
        resolve,
      });
    });
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {dialog && <DialogView opts={dialog.opts} onResult={handleResult} />}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Fallback defensivo: se algum componente usar fora do provider,
    // cai no window.confirm nativo em vez de quebrar. Log um aviso.
    console.warn('[useConfirm] Usado fora do ConfirmProvider — fallback para window.confirm');
    return (input) => {
      const msg = typeof input === 'string' ? input : (input?.message || 'Confirmar?');
      return Promise.resolve(window.confirm(msg));
    };
  }
  return ctx;
}

// ── Componente visual ───────────────────────────────────────────────────────

function DialogView({ opts, onResult }) {
  const variant = VARIANT_STYLES[opts.variant];
  const confirmBtnRef = useRef(null);

  // Foco automático no botão de confirmação (acessibilidade).
  useEffect(() => {
    const t = setTimeout(() => confirmBtnRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, []);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onResult(false);
  };

  return (
    <div
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: 16,
        animation: 'confirm-fade-in 0.15s ease-out',
      }}
    >
      <style>{`
        @keyframes confirm-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes confirm-scale-in {
          from { opacity: 0; transform: scale(0.94) translateY(8px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
      <div
        style={{
          background: 'var(--surface)',
          borderRadius: 16,
          maxWidth: 460,
          width: '100%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.35), 0 0 0 1px var(--gray-200)',
          overflow: 'hidden',
          animation: 'confirm-scale-in 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
          maxHeight: 'calc(100vh - 48px)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header com accent color por variant */}
        <div style={{
          padding: '22px 24px 16px 24px',
          borderBottom: '1px solid var(--gray-200)',
          display: 'flex',
          gap: 14,
          alignItems: 'flex-start',
        }}>
          <div style={{
            width: 44,
            height: 44,
            borderRadius: 22,
            background: `${variant.accent}15`,
            border: `2px solid ${variant.accent}30`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 22,
            flexShrink: 0,
          }}>
            {variant.icon}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3
              id="confirm-dialog-title"
              style={{
                margin: 0,
                fontSize: 17,
                fontWeight: 700,
                color: 'var(--gray-900)',
                lineHeight: 1.35,
              }}
            >
              {opts.title}
            </h3>
          </div>
          <button
            onClick={() => onResult(false)}
            aria-label="Fechar"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--gray-400)',
              cursor: 'pointer',
              fontSize: 20,
              lineHeight: 1,
              padding: 4,
              flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>

        {/* Corpo rolável quando o conteúdo é longo */}
        <div style={{
          padding: '18px 24px',
          overflowY: 'auto',
          flex: 1,
        }}>
          <p style={{
            margin: 0,
            fontSize: 14,
            lineHeight: 1.6,
            color: 'var(--gray-700)',
            whiteSpace: 'pre-wrap',
          }}>
            {opts.message}
          </p>

          {opts.items && opts.items.length > 0 && (
            <ul style={{
              margin: '14px 0 0 0',
              padding: '12px 16px 12px 28px',
              background: 'var(--gray-50)',
              borderRadius: 8,
              borderLeft: `3px solid ${variant.accent}`,
              fontSize: 13,
              color: 'var(--gray-700)',
              lineHeight: 1.7,
            }}>
              {opts.items.map((item, i) => (
                <li key={i} style={{ marginBottom: 2 }}>{item}</li>
              ))}
            </ul>
          )}

          {opts.hint && (
            <div style={{
              marginTop: 14,
              padding: '10px 14px',
              background: 'var(--gray-50)',
              border: '1px solid var(--gray-200)',
              borderRadius: 8,
              fontSize: 12.5,
              color: 'var(--gray-600)',
              lineHeight: 1.55,
            }}>
              💡 {opts.hint}
            </div>
          )}
        </div>

        {/* Footer com botões */}
        <div style={{
          padding: '14px 20px 18px 20px',
          borderTop: '1px solid var(--gray-200)',
          display: 'flex',
          gap: 10,
          justifyContent: 'flex-end',
          flexWrap: 'wrap-reverse',
        }}>
          <button
            className="btn btn-secondary"
            onClick={() => onResult(false)}
            style={{ minWidth: 100 }}
          >
            {opts.cancelText}
          </button>
          <button
            ref={confirmBtnRef}
            className={`btn ${variant.confirmClass}`}
            onClick={() => onResult(true)}
            style={{ minWidth: 100 }}
          >
            {opts.confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmProvider;
